import { ReactNode, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
  useRepository
} from '@city-sage/aurum-avis-labs-components';
import { QueryKeys } from '../lib/QueryKeys';
import { Loader2, User, FileText } from 'lucide-react';
import { useIsAuthenticated } from '@azure/msal-react';

// Zod schema for account name validation
const userNameSchema = z.object({
  userName: z
    .string()
    .min(5, 'Username must be at least 5 characters')
    .max(30, 'Username must be less than 30 characters')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, hyphens, and underscores')
});

// Zod schema for about me validation
const aboutMeSchema = z.object({
  aboutMe: z
    .string()
    .max(500, 'About me must be less than 500 characters')
    .optional()
});

type UserNameFormValues = z.infer<typeof userNameSchema>;
type AboutMeFormValues = z.infer<typeof aboutMeSchema>;

interface AccountNameGuardProps {
  children: ReactNode;
}

export const AccountNameGuard = ({ children }: AccountNameGuardProps) => {
  const { t } = useTranslation();
  const { api } = useRepository();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<'username' | 'aboutme' | 'complete'>('username');
  const isAuthenticated = useIsAuthenticated();
  
  // Get current user's account
  const { data: account, isLoading: isLoadingAccount, error: accountError } = useQuery({
    queryKey: [QueryKeys.account.details()],
    queryFn: () => api.getMyAccount(),
    retry: 1,
    enabled: !!isAuthenticated
  });

  // Set initial step based on account data
  useEffect(() => {
    if (account) {
      if (!account.userName) {
        setCurrentStep('username');
      } else if (currentStep === 'username') {
        // Only transition to aboutme if we just completed username step
        // or if we're checking for the first time and username exists
        setCurrentStep('complete');
      }
    }
  }, [account?.userName, account?.id]);

  // Username form setup
  const userNameForm = useForm<UserNameFormValues>({
    resolver: zodResolver(userNameSchema),
    defaultValues: {
      userName: ''
    }
  });

  // About me form setup
  const aboutMeForm = useForm<AboutMeFormValues>({
    resolver: zodResolver(aboutMeSchema),
    defaultValues: {
      aboutMe: account?.aboutMe || ''
    }
  });

  // Mutation to update account name
  const updateUserNameMutation = useMutation({
    mutationFn: (data: UserNameFormValues) => 
      api.updateAccount({
        id: account!.id,
        updateAccountDto: {
          userName: data.userName
        }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.account.details()] });
      toast.success(t('common.accountName.success'));
      setCurrentStep('aboutme');
    },
    onError: (error) => {
      console.error('Failed to update account name:', error);
      toast.error(t('common.accountName.error'));
    }
  });

  // Mutation to update about me
  const updateAboutMeMutation = useMutation({
    mutationFn: (data: AboutMeFormValues) => 
      api.updateAccount({
        id: account!.id,
        updateAccountDto: {
          aboutMe: data.aboutMe || ''
        }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.account.details()] });
      toast.success(t('common.aboutMe.success'));
      setCurrentStep('complete');
    },
    onError: (error) => {
      console.error('Failed to update about me:', error);
      toast.error(t('common.aboutMe.error'));
    }
  });

  // Form submission handlers
  const onUserNameSubmit = userNameForm.handleSubmit((data) => {
    updateUserNameMutation.mutate(data);
  });

  const onAboutMeSubmit = aboutMeForm.handleSubmit((data) => {
    updateAboutMeMutation.mutate(data);
  });

  const handleSkipAboutMe = () => {
    setCurrentStep('complete');
  };

  // Loading state
  if (isLoadingAccount) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Show username step if user doesn't have a username
  if (account && !account.userName && currentStep === 'username') {
    return (
              <div className="flex items-center justify-center min-h-dvh p-4">
        <Card className="w-full max-w-md backdrop-blur-md bg-white/90 border border-white/30 shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <User className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">
              {t('common.accountName.title')}
            </CardTitle>
            <CardDescription className="text-base">
              {t('common.accountName.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...userNameForm}>
              <form onSubmit={onUserNameSubmit} className="space-y-4">
                <FormField
                  control={userNameForm.control}
                  name="userName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.accountName.label')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('common.accountName.placeholder')}
                          {...field}
                          className="transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={updateUserNameMutation.isPending}
                >
                  {updateUserNameMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.creating')}
                    </>
                  ) : (
                    t('common.accountName.continueButton')
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show about me step
  if (account && account.userName && currentStep === 'aboutme') {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-background p-4">
        <Card className="w-full max-w-md backdrop-blur-md bg-white/90 border border-white/30 shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-white rounded-full">
                <FileText className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">
              {t('common.aboutMe.title')}
            </CardTitle>
            <CardDescription className="text-base">
              {t('common.aboutMe.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...aboutMeForm}>
              <form onSubmit={onAboutMeSubmit} className="space-y-4">
                <FormField
                  control={aboutMeForm.control}
                  name="aboutMe"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.aboutMe.label')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('common.aboutMe.placeholder')}
                          {...field}
                          className="transition-all duration-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[100px]"
                          maxLength={500}
                        />
                      </FormControl>
                      <FormMessage />
                      <div className="text-xs text-muted-foreground text-right">
                        {field.value?.length || 0}/500
                      </div>
                    </FormItem>
                  )}
                />
                <div className="flex gap-3 flex-col sm:flex-row">
                  <Button
                    type="button"
                    variant="ghost"
                    className="flex-1"
                    onClick={handleSkipAboutMe}
                  >
                    {t('common.aboutMe.skipButton')}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={updateAboutMeMutation.isPending}
                  >
                    {updateAboutMeMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.saving')}
                      </>
                    ) : (
                      t('common.aboutMe.saveButton')
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show children (main app) when onboarding is complete
  return <>{children}</>;
};